import { useRef } from "react"
import { useSuspenseQuery } from "@tanstack/react-query"

import { HelpCenterContent, HelpCenterHeader, WikiSection } from "@/areas/help-center"
import DownloadCard from "@/areas/help-center/components/DownloadCard/DownloadCard"
import { ReleasesDao } from "@/entities/releases"
import { AppName, Platform } from "@/entities/releases/releases.types"
import Article from "@/shared/layouts/Article/Article"

import "./older-versions.scss"

// Helper function to format date
const formatReleaseDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric"
  })
}

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i]
}

function OlderVersionsOcbApp() {
  const contentRef = useRef<HTMLDivElement>(null)

  // Fetch all OCB App releases
  const { data: releases } = useSuspenseQuery({
    queryKey: [ReleasesDao.name, "list", AppName.OcbApp],
    queryFn: async () => {
      try {
        const response = await ReleasesDao.listReleases({
          app: AppName.OcbApp,
          prerelease: false,
          limit: 50,
          page: 1
        })
        return response.data
      } catch (error) {
        console.error("Failed to fetch OCB App releases:", error)
        throw error
      }
    },
  })

  // Get the latest release for the featured card
  const latestRelease = releases[0]

  // Get older versions (all except the first one)
  const olderReleases = releases.slice(1)

  // Fetch download URL for latest release
  const { data: latestDownloadData } = useSuspenseQuery({
    queryKey: [ReleasesDao.name, "download", latestRelease?.id],
    queryFn: async () => {
      if (!latestRelease) return null

      try {
        const asset = latestRelease.assets.find(a => a.platform === Platform.Windows)
        if (!asset) return null

        const downloadUrl = await ReleasesDao.getDownloadUrl(asset.s3Key)
        return {
          name: "OCB App",
          version: latestRelease.tagName,
          releaseDate: formatReleaseDate(latestRelease.publishedAt),
          downloadUrl: downloadUrl.url
        }
      } catch (error) {
        console.error("Failed to fetch download URL:", error)
        return null
      }
    },
    enabled: !!latestRelease
  })

  const handleDownloadOlderVersion = async (releaseId: string) => {
    try {
      const release = releases.find(r => r.id === releaseId)
      if (!release) return

      const asset = release.assets.find(a => a.platform === Platform.Windows)
      if (!asset) {
        console.warn("No Windows asset found for this release")
        return
      }

      const downloadUrl = await ReleasesDao.getDownloadUrl(asset.s3Key)
      window.open(downloadUrl.url, "_blank")
    } catch (error) {
      console.error("Failed to download version:", error)
    }
  }

  return (
    <HelpCenterContent ref={contentRef}>
      <HelpCenterHeader
        logo="/static/wiki/downloadIcon.png"
        title="STREAM REQUIRED DOWNLOADS"
        subtitle="Download the latest versions of all applications and files required for your Stream setup."
        backPath="/help-center/getting-started"
        backLabel="Back"
        contentRef={contentRef}
        hidePdfDownload={true}
      />

      <WikiSection id="older-versions-ocb" title="OLDER VERSIONS OF OCB APP">
        <Article>
          {/* Featured Latest Version Card */}
          {latestDownloadData && (
            <div className="wiki-downloads-grid" style={{ marginBottom: "2rem" }}>
              <DownloadCard
                name={latestDownloadData.name}
                version={latestDownloadData.version}
                releaseDate={latestDownloadData.releaseDate}
                downloadUrl={latestDownloadData.downloadUrl}
              />
            </div>
          )}

          {/* List of Older Versions */}
          <div className="older-versions-list">
            {olderReleases.map((release) => {
              const windowsAsset = release.assets.find(a => a.platform === Platform.Windows)

              return (
                <div key={release.id} className="older-version-item">
                  <div className="older-version-item__info">
                    <div className="older-version-item__header">
                      <div className="older-version-item__version">
                        Version: {release.tagName}
                      </div>
                    </div>
                    <div className="older-version-item__meta">
                      <div className="older-version-item__date">
                        Release Date: {formatReleaseDate(release.publishedAt)}
                      </div>
                      {release.releaseNotes && release.releaseNotes.trim() && (
                        <div className="older-version-item__notes">
                          {release.releaseNotes.trim()}
                        </div>
                      )}
                    </div>
                  </div>
                  {windowsAsset && (
                    <div className="older-version-item__file-info">
                      <span className="older-version-item__file-name">{windowsAsset.name}</span>
                      <span className="older-version-item__file-size">
                        {formatFileSize(windowsAsset.size)}
                      </span>
                    </div>
                  )}
                  <button
                    className="older-version-item__download"
                    onClick={() => handleDownloadOlderVersion(release.id)}
                    aria-label={`Download ${release.tagName}`}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 13L6 9H8V4H12V9H14L10 13Z" fill="currentColor"/>
                      <path d="M4 15H16V17H4V15Z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              )
            })}
          </div>
        </Article>
      </WikiSection>
    </HelpCenterContent>
  )
}

export default OlderVersionsOcbApp
